import { createTheme, Theme } from '@mui/material/styles'

// NCESP Brand Colors (from constitutional requirements)
const brandColors = {
  navy: '#211e4c', // Primary dark blue
  red: '#962029', // Primary red
  white: '#ffffff',
  lightGray: '#f5f5f5',
  darkGray: '#333333',
}

// Extended color palette
const colors = {
  primary: {
    main: brandColors.navy,
    light: '#4a47a3',
    dark: '#161439',
    contrastText: brandColors.white,
  },
  secondary: {
    main: brandColors.red,
    light: '#c44f56',
    dark: '#6b161c',
    contrastText: brandColors.white,
  },
  background: {
    default: brandColors.white,
    paper: brandColors.white,
    accent: brandColors.lightGray,
  },
  text: {
    primary: brandColors.darkGray,
    secondary: brandColors.navy,
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
}

// Typography configuration
const typography = {
  fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: colors.primary.main,
  },
  h2: {
    fontSize: '2.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: colors.primary.main,
  },
  h3: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: colors.primary.main,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: colors.primary.main,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.5,
    color: colors.primary.main,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.5,
    color: colors.primary.main,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.6,
    color: colors.text.primary,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: colors.text.primary,
  },
  subtitle1: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.5,
    color: colors.text.secondary,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    color: colors.text.secondary,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: colors.text.primary,
  },
}

// Component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: '12px 24px',
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.02em',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.12)',
        },
      },
      contained: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        paddingLeft: 24,
        paddingRight: 24,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 2px 8px rgba(33, 30, 76, 0.1)',
      },
    },
  },
}

// Spacing system
const spacing = 8

// Breakpoints
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
}

// Create the theme
export const theme: Theme = createTheme({
  palette: colors,
  typography,
  components,
  spacing,
  breakpoints,
  shape: {
    borderRadius: 8,
  },
})

export default theme
